/**
 * @file HttpError
 * @author Eric Xu <xuxiang@zhichetech.com>
 * @copyright: (c) 2019-2020 sichuan zhichetech co., ltd.
 */

import type { ResponseContext } from './types';

/**
 * @class
 * @extends {Error}
 * The http error class w/ http related information.
 */
/**
 * The structured payload beside `code` / `subCode`: `i18n` holds the values
 * the server filled into its sentence, `data` what a client acts on (see
 * `requestErrorDetails.ts`).
 */
export type HttpErrorDetails = {
  i18n?: Record<string, string | number>;
  data?: unknown;
};

export class HttpError<T = any> extends Error {
  code?: string;
  subCode?: string;
  extra?: T;
  details?: HttpErrorDetails;
  status = 0;
  statusText: string | undefined;
  response: any;
  // outcome of the reactive token refresh attempted for a 401, if any.
  authRefreshStatus?: ResponseContext['authRefreshStatus'];

  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(message: string | undefined) {
    super(message);
  }
}

/**
 * Whether the server answered that the resource is not there. By status as
 * well as code: a 404 whose body lost its code on the way is still a 404.
 */
export function isNotFoundHttpError(error: unknown): boolean {
  return (
    error instanceof HttpError &&
    (error.code === 'resource_not_found' || error.status === 404)
  );
}
